import React from "react";
import { Button, Spinner } from "react-bootstrap";
import { PermissionWrapper } from "../../../../components/PermissionWrapper/PermissionWrapper";
import { ENROLMENTS_MANAGE_PERMS } from "../../permissions";
import { useOfflineEnrolmentForm, type OfflineEnrolmentFormMode } from "./useOfflineEnrolmentForm";
import type { SelectedSlot } from "../useEnrolmentForm";
import type { EnrolmentDetails, BatchDetails } from "../../../../api/academyEnrolments/enrolmentsService";

import AcademySection from "../sections/AcademySection";
import CenterSection from "../sections/CenterSection";
import AgeCategorySection from "../sections/AgeCategorySection";
import BatchSection from "../sections/BatchSection";
import SlotSection from "../sections/SlotSection";
import FrequencyAndPaymentSection from "../sections/FrequencyAndPaymentSection";

import OfflinePlayerSection from "./OfflinePlayerSection";
import OfflineScheduleSection from "./OfflineScheduleSection";

import "../EnrolmentForm.css";

interface OfflineEnrolmentFormProps {
  mode?: OfflineEnrolmentFormMode;
  initialEnrolment?: EnrolmentDetails | null;
  initialBatch?: BatchDetails | null;
  onSuccess?: () => void;
}

const OfflineEnrolmentForm: React.FC<OfflineEnrolmentFormProps> = ({
  mode = "add",
  initialEnrolment,
  initialBatch,
  onSuccess,
}) => {
  const {
    state,
    loading,
    submitError,
    setSubmitError,
    filteredPlayers,
    filteredBatchList,
    availablePaymentFrequencies,
    planFields,
    hasPlayer,
    allowPlayerAttach,
    handlers,
  } = useOfflineEnrolmentForm({ mode, initialEnrolment, initialBatch, onSuccess });

  const batch = state.selectedBatch;
  const slotType = batch?.slotType ?? "fixed";
  const fixedSlots = batch?.fixedSlots ?? [];
  const dynamicSlots = (batch?.slots ?? []) as Parameters<typeof SlotSection>[0]["dynamicSlots"];
  const fees = batch?.fees ?? [];

  const isView = mode === "view";
  const isDynamic = slotType === "dynamic";

  const hasAcademy = !!state.selectedAcademy;
  const hasSport = !!state.selectedSport;
  const hasCenter = !!state.selectedCenter;
  const hasAgeCategory = !!state.selectedAgeCategory;
  const hasBatch = !!state.selectedBatch;
  const hasSlot = isDynamic
    ? state.selectedDynamicSlots.length > 0
    : !!state.selectedSlot;
  const hasTrainingFreq = !!state.selectedTrainingFrequency;

  const canSubmit = hasCenter;

  const handleSubmitClick = async () => {
    setSubmitError(null);
    await handlers.handleSubmit();
  };

  return (
    <div className="enrolment-form-layout">
      <div>
        {/* Section 1 — Player */}
        <OfflinePlayerSection
          mode={mode}
          allowPlayerAttach={allowPlayerAttach}
          state={state}
          loading={loading}
          filteredPlayers={filteredPlayers}
          onPlayerModeChange={handlers.handlePlayerModeChange}
          onPhoneChange={handlers.setPhoneNumber}
          onSearch={handlers.handlePhoneSearch}
          onCreateWebsiteUser={handlers.handleCreateWebsiteUser}
          onShowAddProfile={handlers.setShowAddProfileForm}
          onAddProfile={handlers.handleAddProfile}
          onRegisterByChange={handlers.handleRegisterByChange}
          onPlayerSelect={handlers.handlePlayerSelect}
          setNewParentName={handlers.setNewParentName}
          setNewParentGender={handlers.setNewParentGender}
          setNewParentDob={handlers.setNewParentDob}
          setNewPlayerName={handlers.setNewPlayerName}
          setNewPlayerGender={handlers.setNewPlayerGender}
          setNewPlayerDob={handlers.setNewPlayerDob}
          onDirectPlayerField={handlers.setDirectPlayerField}
          onCreateDirectPlayer={handlers.handleCreateDirectPlayer}
          onResetDirectPlayer={handlers.handleResetDirectPlayer}
        />

        {/* Section 2 — Schedule & Payment (always visible) */}
        <OfflineScheduleSection
          startDate={state.startDate}
          endDate={state.endDate}
          paidAt={state.paidAt}
          paymentAmount={state.paymentAmount}
          onStartDateChange={handlers.handleStartDateChange}
          onEndDateChange={handlers.handleEndDateChange}
          onPaidAtChange={handlers.handlePaidAtChange}
          onPaymentAmountChange={handlers.handlePaymentAmountChange}
          sectionNumber={2}
          disabled={isView}
        />

        {/* Section 3 — Academy + Sport (always visible) */}
        <AcademySection
          academyList={state.academyList}
          selectedAcademy={state.selectedAcademy}
          selectedSport={state.selectedSport}
          loadingAcademies={loading.academies}
          onAcademySelect={handlers.handleAcademySelect}
          onSportSelect={handlers.handleSportSelect}
          disabled={isView}
        />

        {/* Section 4 — Center */}
        {((hasAcademy && hasSport) || isView) && (
          <CenterSection
            centerList={state.centerList}
            selectedCenter={state.selectedCenter}
            loadingCenters={loading.centers}
            onCenterSelect={handlers.handleCenterSelect}
            disabled={isView}
          />
        )}

        {/* Section 5 — Age Category */}
        {(hasCenter || isView) && (
          <AgeCategorySection
            ageCategoryList={state.ageCategoryList}
            selectedAgeCategory={state.selectedAgeCategory}
            loadingAgeCategories={loading.ageCategories}
            onAgeCategorySelect={handlers.handleAgeCategorySelect}
            disabled={isView}
          />
        )}

        {/* Section 6 — Batch */}
        {(hasAgeCategory || isView) && (
          <BatchSection
            batchList={filteredBatchList}
            selectedBatch={state.selectedBatch}
            loadingBatches={loading.batches}
            onBatchSelect={handlers.handleBatchSelect}
            disabled={isView}
            sectionNumber={6}
          />
        )}

        {/* Section 7 — Slot */}
        {(hasBatch || isView) && (
          <SlotSection
            slotType={slotType}
            fixedSlots={fixedSlots}
            dynamicSlots={dynamicSlots}
            selectedSlot={state.selectedSlot as SelectedSlot | null}
            onSlotSelect={handlers.handleSlotSelect}
            selectedDynamicSlots={state.selectedDynamicSlots as SelectedSlot[]}
            onDynamicSlotToggle={handlers.handleDynamicSlotToggle}
            disabled={isView}
            sectionNumber={7}
          />
        )}

        {/* Section 8 — Training + Payment Frequency */}
        {((hasSlot && fees.length > 0) || isView) && (
          <FrequencyAndPaymentSection
            fees={fees}
            selectedTrainingFrequency={state.selectedTrainingFrequency}
            selectedPaymentFrequency={state.selectedPaymentFrequency}
            availablePaymentFrequencies={availablePaymentFrequencies}
            planFields={planFields}
            onTrainingFrequencySelect={handlers.handleTrainingFrequencySelect}
            onPaymentFrequencySelect={handlers.handlePaymentFrequencySelect}
            disabled={isView}
            sectionNumber={8}
          />
        )}

        {/* Submit error */}
        {submitError && (
          <div className="enrolment-submit-error mb-3">
            <i className="ri-error-warning-line me-2" />
            {submitError}
          </div>
        )}

        {/* Submit button — hidden in view mode */}
        {!isView && (
          <PermissionWrapper
            anyOfPermissions={ENROLMENTS_MANAGE_PERMS}
            hideWhenDisabled={false}
          >
            <Button
              variant="primary"
              disabled={!canSubmit || loading.submit}
              onClick={handleSubmitClick}
              style={{ minWidth: 160 }}
            >
              {loading.submit && (
                <Spinner size="sm" animation="border" className="me-2" />
              )}
              {mode === "edit" ? "Update Offline Enrolment" : "Create Offline Enrolment"}
            </Button>
          </PermissionWrapper>
        )}
      </div>
    </div>
  );
};

export default OfflineEnrolmentForm;
